import { BreadcrumbsContainer } from "@components/atoms/Breadcrumbs"
import { Chip } from "@components/atoms/Chip"
import { Heading } from "@components/atoms/Heading"
import { Col, Container, Grid } from "@components/layout/Grid"
import { cn } from "@utils/tw"
import { ContentBlocks } from "eddev/blocks"
import { useHydrating } from "eddev/routing"
import { defineView } from "eddev/views"
import { ComponentPropsWithoutRef, forwardRef, useMemo } from "react"
import { format } from "date-fns"
import { Typography } from "@components/atoms/Typography"
import { VSpace } from "@components/layout/Spacer"
import { Button } from "@components/atoms/Button"
import { Share } from "@components/molecules/Share"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { useIsMobile } from "@hooks/useViewportDerived"
import { LatestNewsSlider } from "@components/molecules/LatestNewsSlider"
import { ArticleCardFragment } from "../types.graphql"
import { useNewsArchive } from "@hooks/queries"
import { FooterCTA } from "@components/atoms/FooterCTA"

export default defineView("single", (props) => {
  const isMobile = useIsMobile()
  const hydrating = useHydrating()
  const isMediaRelease = useMemo(() => {
    if (props.post?.tags) return props.post.tags.nodes.some((node) => node.slug === "media-release")
  }, [props])

  const chips = useMemo(() => {
    return props.post?.categories?.nodes?.concat(props.post.tags?.nodes ?? [])
  }, [props])

  const exerpt = useMemo(() => {
    if (props.post?.excerpt) {
      if (!props.post.excerpt.includes("[&hellip;]")) return props.post.excerpt
    }
    const paragraphs = props.post?.contentBlocks.filter((block: any) => block.blockName === "core/paragraph")
    if (paragraphs && paragraphs?.length > 0) return paragraphs[0].innerHTML
    return
  }, [props])

  const remainingBlocks = useMemo(() => {
    const blocks = props.post?.contentBlocks
    if (!blocks) return []

    if (props.post?.excerpt) {
      if (!props.post.excerpt.includes("[&hellip;]")) return blocks
    }

    let foundFirstParagraph = false
    const remaining = blocks.filter((block: any) => {
      if (block.blockName === "core/paragraph" && !foundFirstParagraph) {
        foundFirstParagraph = true
        return false
      }
      return true
    })

    return remaining
  }, [props])

  const curatedArticles: (ArticleCardFragment | null)[] = useMemo(() => {
    if (props.post?.relatedArticles?.relatedArticles) {
      if (props.post.relatedArticles.relatedArticles.length > 0) return props.post.relatedArticles.relatedArticles
    }
    return []
  }, [props])

  const relatedArticles = useNewsArchive({
    length: 5,
    tag: props.post?.tags?.nodes.map((tag) => tag.slug).join(","),
    category: props.post?.categories?.nodes.map((cat) => cat.slug).join(","),
  })

  return (
    <>
      <BreadcrumbsContainer>
        <div className="grid grid-cols-12 xxl:px-0 !w-[100vw] xl:!max-w-[1600px] xxl:!max-w-[1720px] mx-auto gap-6">
          <div className="col-span-12">
            <ul>
              <li>
                <Chip color="white" href="/newsroom">
                  Newsroom
                </Chip>
              </li>
              <li>
                <Chip color="white" href="/newsroom/news-archive">
                  News archive
                </Chip>
              </li>
            </ul>
          </div>
        </div>
      </BreadcrumbsContainer>

      <div className="grid-auto gap-6">
        <div className="col-span-12 md:col-span-10 md:col-start-2 xl:col-span-8 xl:col-start-3">
          <h1 className="!type-title-l">{props.post?.title}</h1>
        </div>

        <div className="col-span-12 md:col-span-10 md:col-start-2 xl:col-span-8 xl:col-start-3">
          <Categories>
            {chips?.map((category, i) => (
              <Chip key={i} color="grey" href={category?.uri ?? "#"}>
                {category.name}
              </Chip>
            ))}
            {props.post?.date && (
              <span className="type-label-caps text-dark-grey flex items-center">
                {format(props.post.date, "dd.MM.yyyy")}
              </span>
            )}
          </Categories>
        </div>

        <div className="col-span-12 md:col-span-10 md:col-start-2 xl:col-span-8 xl:col-start-3">
          <Excerpt dangerouslySetInnerHTML={{ __html: exerpt ?? "" }} />
        </div>

        <div className="col-span-12 md:col-span-10 md:col-start-2 xl:col-span-8 xl:col-start-3">
          <Share title={props.post?.title ?? ""} />
        </div>

        {props.post?.featuredImage && (
          <>
            <div className="col-span-12 md:col-span-10 md:col-start-2 xl:col-span-8 xl:col-start-3 py-2">
              <HeroContainer>
                <ResponsiveImage {...props.post.featuredImage.node} aspect={16 / 9} />
              </HeroContainer>
            </div>
          </>
        )}

        <div className="col-span-12 md:col-span-10 md:col-start-2 xl:col-span-8 xl:col-start-3">
          <PageContent>
            <ContentBlocks
              blocks={remainingBlocks}
              wrapBlock={(child) => {
                if (child.props?.block) {
                  if (child.props.block?.[0]?.includes("acf")) {
                    return <NegativeMargin>{child}</NegativeMargin>
                  }
                }
                return child
              }}
            />
          </PageContent>
        </div>

        <div className="col-span-12 md:col-span-10 md:col-start-2 xl:col-span-8 xl:col-start-3">
          <Share title={props.post?.title ?? ""} end />
        </div>

        {isMediaRelease && (
          <>
            <div className="col-span-12 md:col-span-10 md:col-start-2 xl:col-span-8 xl:col-start-3">
              <VSpace initial={3} tablet={5} />
              <MediaContactContainer>
                <Heading as="h5" variant={isMobile ? "h5" : "h4"}>
                  Media contact
                </Heading>
                <Button color="white" size="medium" icon="EMAIL" iconStyle="left" href="mailto:media@aaa.asn.au">
                  media@aaa.asn.au
                </Button>
              </MediaContactContainer>
              <VSpace initial={3} tablet={5} />
            </div>
          </>
        )}

        {props.post?.footerCTA?.ctaMessage && props.post?.footerCTA?.link?.title && (
          <div className="col-span-12">
            <FooterCTA>
              <Heading
                as="h4"
                variant={isMobile ? "h5" : "h4"}
                dangerouslySetInnerHTML={{ __html: props.post.footerCTA.ctaMessage }}
              />
              <Button
                color="outline"
                size="medium"
                icon="ARROW_RIGHT"
                iconStyle="right"
                href={props.post.footerCTA.link.url ?? ""}
                target={props.post.footerCTA.link.target ? "_blank" : undefined}
              >
                {props.post.footerCTA.link.title}
              </Button>
            </FooterCTA>
          </div>
        )}

        {!hydrating && relatedArticles.data?.posts?.nodes && (
          <div className="col-span-12">
            <LatestNewsSlider
              title={props.post?.relatedArticles?.relatedArticleTitle ?? "Related Articles"}
              articles={
                curatedArticles.length > 0
                  ? (curatedArticles as ArticleCardFragment[])
                  : relatedArticles.data.posts.nodes
              }
            />
          </div>
        )}
      </div>
    </>
  )
})

const Excerpt = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("[&>p]:type-title-s", className)} {...props} />
))
Excerpt.displayName = "Excerpt"

const NegativeMargin = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "md:ml-[calc(((var(--tw-grid-inner-width)-(var(--tw-grid-gutter)*11))/12+var(--tw-grid-gutter))*-1)]",
      "lg:ml-[calc(((var(--tw-grid-inner-width)-(var(--tw-grid-gutter)*11))/12+var(--tw-grid-gutter))*-2)]",
      className,
    )}
    {...props}
  />
))
NegativeMargin.displayName = "NegativeMargin"

const SingleTemplate = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof Container>>(
  ({ className, ...props }, ref) => (
    <Container
      ref={ref}
      className={cn("pb-5 [&_h1]:mt-8 [&_h1]:mb-5 [&_h1]:inline-block md:[&_h1]:mt-9", className)}
      {...props}
    />
  ),
)
SingleTemplate.displayName = "SingleTemplate"

const Categories = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex gap-x-3 gap-y-1 flex-wrap my-2 relative z-[1] pointer-events-auto", className)}
    {...props}
  />
))
Categories.displayName = "Categories"

const HeroContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("rounded-md overflow-hidden", className)} {...props} />
))
HeroContainer.displayName = "HeroContainer"

const PageContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col",
      "[&_h2]:inline-block [&_h2]:mt-6 [&_h2]:mb-2",
      "[&_h3]:inline-block [&_h3]:mt-6 [&_h3]:mb-2",
      "[&_h4]:inline-block [&_h4]:mt-6 [&_h4]:mb-2",
      "[&_h5]:inline-block [&_h5]:mt-6 [&_h5]:mb-2",
      "[&_figure.wp-block-table]:my-4",
      "[&_figure.wp-block-table_figcaption]:py-2 [&_figure.wp-block-table_figcaption]:type-label-s [&_figure.wp-block-table_figcaption]:italic",
      "[&_ul]:flex [&_ul]:flex-col [&_ul]:pl-4",
      "[&_ul_li]:list-disc [&_ul_li]:py-2",
      "[&_div>p]:my-4 [&_div>p]:type-body-m",
      className,
    )}
    {...props}
  />
))
PageContent.displayName = "PageContent"

const MediaContactContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("bg-light-grey rounded-md p-6 flex justify-between items-center md:py-4 md:px-5", className)}
      {...props}
    />
  ),
)
MediaContactContainer.displayName = "MediaContactContainer"
